import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from './icon';

/** Общие элементы Mini App. Палитра фирменная: бордо, золото, крем. */

/**
 * Обёртка экрана. `title` рисует шапку с кнопкой «назад» — её ставят вложенные
 * экраны, у которых нет нижних вкладок. У вкладок заголовок обычный, без кнопки.
 */
export function Screen({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <>
      {title && <SubHeader title={title} />}
      <div className="flex min-h-full flex-col gap-6 px-4 pb-8 pt-2 sm:px-6">{children}</div>
    </>
  );
}

/** Шапка вложенного экрана: кнопка «назад» вместо нижних вкладок. */
export function SubHeader({ title, action }: { title: string; action?: ReactNode }) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <header className="sticky top-0 z-30 flex items-center gap-2 bg-cream/95 px-4 py-3 backdrop-blur sm:px-6">
      <button
        type="button"
        onClick={() => navigate(-1)}
        aria-label={t('common.back')}
        className="grid size-10 shrink-0 place-items-center rounded-full bg-surface shadow-soft active:scale-95"
      >
        <Icon name="back" size={20} />
      </button>
      <h1 className="min-w-0 flex-1 truncate font-display text-xl font-semibold">{title}</h1>
      {action}
    </header>
  );
}

export function SectionTitle({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div className="mb-3 flex items-baseline justify-between gap-3">
      <h2 className="font-display text-[22px] font-semibold leading-tight">{children}</h2>
      {action}
    </div>
  );
}

export function Skeleton({ className = '' }: { className?: string }) {
  return <div className={`skeleton rounded-xl ${className}`} />;
}

export function CardSkeleton() {
  return (
    <div>
      <Skeleton className="aspect-[3/4] w-full rounded-card" />
      <Skeleton className="mt-2 h-4 w-3/4" />
      <Skeleton className="mt-1 h-3 w-1/2" />
    </div>
  );
}

export function EmptyState({
  title,
  hint,
  action,
}: {
  title: string;
  hint?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-2 py-16 text-center">
      <div className="mb-1 grid size-16 place-items-center rounded-full bg-line">
        <Icon name="search" size={26} className="text-muted" />
      </div>
      <p className="font-display text-lg">{title}</p>
      {hint && <p className="max-w-xs text-sm text-muted">{hint}</p>}
      {action}
    </div>
  );
}

export function ErrorState({ onRetry }: { onRetry?: () => void }) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col items-center gap-3 py-16 text-center">
      <p className="text-sm text-muted">{t('common.error')}</p>
      {onRetry && (
        <Button variant="secondary" size="sm" onClick={onRetry}>
          {t('common.retry')}
        </Button>
      )}
    </div>
  );
}

type Variant = 'primary' | 'secondary' | 'ghost' | 'gold' | 'danger';
type Size = 'sm' | 'md';

const VARIANTS: Record<Variant, string> = {
  primary: 'bg-bordo text-white active:bg-bordo-dark',
  secondary: 'border border-bordo bg-surface text-bordo active:bg-bordo/5',
  ghost: 'text-bordo active:bg-bordo/5',
  gold: 'bg-gold text-white active:brightness-95',
  danger: 'bg-error text-white active:brightness-95',
};

const SIZES: Record<Size, string> = {
  sm: 'h-10 px-4 text-sm',
  md: 'h-[52px] px-6 text-base',
};

export function Button({
  variant = 'primary',
  size = 'md',
  className = '',
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; size?: Size }) {
  return (
    <button
      {...props}
      className={`inline-flex items-center justify-center gap-2 rounded-xl font-semibold transition active:scale-[0.97] disabled:pointer-events-none disabled:opacity-40 ${VARIANTS[variant]} ${SIZES[size]} ${className}`}
    />
  );
}

export function Chip({
  active,
  children,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { active?: boolean }) {
  return (
    <button
      {...props}
      className={`shrink-0 rounded-pill px-3.5 py-1.5 text-sm transition active:scale-95 ${
        active ? 'bg-bordo text-white' : 'bg-surface text-ink shadow-soft'
      }`}
    >
      {children}
    </button>
  );
}

export function Card({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={`rounded-card bg-surface p-4 shadow-soft ${className}`}>{children}</div>;
}

export function Row({ label, value, hint }: { label: string; value: ReactNode; hint?: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1">
      <span className="text-sm text-muted">
        {label}
        {hint && <span className="block text-xs opacity-80">{hint}</span>}
      </span>
      <span className="text-sm font-semibold text-ink">{value}</span>
    </div>
  );
}

export function Badge({
  tone = 'neutral',
  children,
}: {
  tone?: 'neutral' | 'green' | 'red' | 'gold';
  children: ReactNode;
}) {
  const tones = {
    neutral: 'bg-line text-muted',
    green: 'bg-success/12 text-success',
    red: 'bg-error/12 text-error',
    gold: 'bg-gold/15 text-bordo-dark',
  };

  return (
    <span className={`shrink-0 rounded-pill px-2 py-0.5 text-xs font-medium ${tones[tone]}`}>
      {children}
    </span>
  );
}

/** Флажок в фирменном стиле — системный не совпадает ни с палитрой, ни с радиусами. */
export function Checkbox({
  checked,
  onChange,
  label,
  className = '',
}: {
  checked?: boolean;
  onChange?: (checked: boolean) => void;
  label?: ReactNode;
  className?: string;
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={Boolean(checked)}
      onClick={() => onChange?.(!checked)}
      className={`flex items-start gap-3 text-left text-sm ${className}`}
    >
      <span
        className={`mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-md border transition ${
          checked ? 'border-bordo bg-bordo text-white' : 'border-line bg-surface'
        }`}
      >
        {checked && <span className="text-xs leading-none">✓</span>}
      </span>
      {label}
    </button>
  );
}

/**
 * Список выбора. Нативный `<select>` на телефоне раскрывается системным
 * виджетом, который игнорирует и палитру, и скругления, поэтому выбор
 * показываем своей шторкой снизу — привычным для мобильных жестом.
 */
export function Select({
  value,
  onChange,
  options,
  className = '',
}: {
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const current = options.find((option) => option.value === value);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`flex items-center gap-1.5 rounded-xl bg-surface px-3 py-2 text-sm shadow-soft ${className}`}
      >
        {current?.label ?? '—'}
        <span className="text-[10px] text-muted">▼</span>
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-end bg-ink/40" onClick={() => setOpen(false)}>
          <div
            className="w-full rounded-t-card bg-surface p-2 pb-[calc(env(safe-area-inset-bottom)+0.5rem)]"
            onClick={(e) => e.stopPropagation()}
          >
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
                className={`flex w-full items-center justify-between rounded-xl px-4 py-3 text-left text-sm ${
                  option.value === value ? 'bg-cream font-medium text-bordo' : ''
                }`}
              >
                {option.label}
                {option.value === value && <span className="text-xs">✓</span>}
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
